// information-school-enrollment.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, FormControl, AbstractControl, ValidatorFn } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { CreateApplicationPayload, CreateApplicationResponse, GetTimeslotsResponse, Timeslot, GroupedTimeslot, GetTermApiResponse, ApiTermData} from '../../core/models/auth.model'; 
import { HttpErrorResponse } from '@angular/common/http';
import { SchoolService } from '../../core/services/school.service'; 
import { Router, RouterLink } from '@angular/router';
import { SettingsOffcanvasComponent } from '../../settings-offcanvas/settings-offcanvas.component';
@Component({
  selector: 'app-information-school-enrollment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterLink,SettingsOffcanvasComponent
  ],
  templateUrl: './information-school-enrollment.component.html',
  styleUrl: './information-school-enrollment.component.css'
})
export class InformationSchoolEnrollmentComponent implements OnInit {
  schoolEnrollmentForm!: FormGroup;
  groupedTimeslots: GroupedTimeslot[] = [];
  allTimeslotsMap: Map<number, Timeslot> = new Map();
  physicallyChallengedOptions = [
    { key: 'musculoskeletal_disorder', labelKey: 'SCHOOL_ENROLLMENT.MUSCULOSKELETAL_DISORDER' },
    { key: 'sensory_impairment', labelKey: 'SCHOOL_ENROLLMENT.SENSORY_IMPAIRMENT' },
    { key: 'chronic_illness', labelKey: 'SCHOOL_ENROLLMENT.CHRONIC_ILLNESS' },
    { key: 'mixed_hereditary_disorders', labelKey: 'SCHOOL_ENROLLMENT.MIXED_HEREDITARY_DISORDERS' }
  ];

  intellectuallyChallengedOptions = [
    { key: 'intellectually_challenged_sub', labelKey: 'SCHOOL_ENROLLMENT.INTELLECTUALLY_CHALLENGED_SUB' },
    { key: 'developmental_disorder', labelKey: 'SCHOOL_ENROLLMENT.DEVELOPMENTAL_DISORDER' },
    { key: 'communication_or_learning_disability', labelKey: 'SCHOOL_ENROLLMENT.COMMUNICATION_LEARNING_DISABILITY' },
    { key: 'disruptive_behavioral_orders', labelKey: 'SCHOOL_ENROLLMENT.DISRUPTIVE_BEHAVIOURAL_DISORDERS' }
  ];
  static atLeastOneDisabilitySelectedValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!(control instanceof FormGroup)) {
        return null; 
      }
      const formGroup = control as FormGroup; 
      const physicallyChallengedGroup = formGroup.get('physically_challenged_group') as FormGroup;
      const intellectuallyChallengedGroup = formGroup.get('intellectually_challenged_group') as FormGroup;

      let isAtLeastOneChecked = false; 
      if (physicallyChallengedGroup) {
        for (const key in physicallyChallengedGroup.controls) {
          if (physicallyChallengedGroup.controls.hasOwnProperty(key)) {
            if (physicallyChallengedGroup.controls[key].value === true) {
              isAtLeastOneChecked = true;
              break; 
            }
          }
        }
      }
      if (!isAtLeastOneChecked && intellectuallyChallengedGroup) {
        for (const key in intellectuallyChallengedGroup.controls) {
          if (intellectuallyChallengedGroup.controls.hasOwnProperty(key)) {
            if (intellectuallyChallengedGroup.controls[key].value === true) {
              isAtLeastOneChecked = true;
              break;
            }
          }
        }
      }
      return isAtLeastOneChecked ? null : { 'atLeastOneDisabilityRequired': true };
    };
  }
  showPreview = false;
  applicationPayload: CreateApplicationPayload | null = null;
  currentTerm: ApiTermData | null = null; 

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private translate: TranslateService,
    private schoolService: SchoolService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getTimeslots();
    this.loadTermData();
  }


  loadTermData(): void {
    this.schoolService.getTerms().subscribe({
      next: (response: GetTermApiResponse) => {
        if (response.status === 'ok' && response.term) {
          this.currentTerm = response.term;
        } else {
          console.error('获取学期数据失败或数据结构不正确:', response.message || response.error || '未知错误');
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('获取学期数据时发生网络错误:', error);
      }
    });
  }

  static atLeastOneCheckboxRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }

      const formGroup = control as FormGroup;
      let isAtLeastOneChecked = false;
      for (const key in formGroup.controls) {
        if (formGroup.controls.hasOwnProperty(key)) {
          if (formGroup.controls[key].value === true) {
            isAtLeastOneChecked = true;
            break;
          }
        }
      }

      return isAtLeastOneChecked ? null : { 'atLeastOneRequired': true };
    };
  }
  get areDisabilitiesSelected(): boolean {
    const errors = this.schoolEnrollmentForm.errors;
    return !errors || !errors['atLeastOneDisabilityRequired'];
  }
  initForm(): void {
    this.schoolEnrollmentForm = this.fb.group({
      organization: ['', Validators.required],
      address: ['', Validators.required],
      tel: ['', Validators.required],
      fax: ['', Validators.required],
      contact_person: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      website: ['', Validators.required],

      physically_challenged_group: this.fb.group({
        musculoskeletal_disorder: [false],
        sensory_impairment: [false],
        chronic_illness: [false],
        mixed_hereditary_disorders: [false]
      }),
      intellectually_challenged_group: this.fb.group({
        intellectually_challenged_sub: [false],
        developmental_disorder: [false],
        communication_or_learning_disability: [false],
        disruptive_behavioral_orders: [false]
      }),

      age_range: ['', Validators.required],
      
      type: ['term_rider', Validators.required],
      prefer_timeslots: this.fb.array([], Validators.required)
    }, {
      validators: InformationSchoolEnrollmentComponent.atLeastOneDisabilitySelectedValidator()
    });
  }

  getTimeslots(): void {
    this.authService.getTimeslotsByType('term_rider').subscribe({
      next: (response: GetTimeslotsResponse) => {
        if (response.status === 'ok' && response.timeslots) {
          // console.log('成功获取时间段信息:', response.timeslots);
          this.groupedTimeslots = this.getTimeslotsGrouped(response.timeslots);
          response.timeslots.forEach(slot => {
            this.allTimeslotsMap.set(slot.id, slot);
          });
          // console.log('分组后的时间段信息:', this.groupedTimeslots);
        } else {
          console.error('获取时间段信息失败:', response.message || response.error || '未知错误');
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('获取时间段信息时发生网络错误:', error);
      }
    });
  }

  getTimeslotsGrouped(timeslots: Timeslot[]): GroupedTimeslot[] {
    return timeslots.reduce((acc, slot) => {
      const existingVenue = acc.find(item => item.venue_id === slot.venue_id);
      if (existingVenue) {
        existingVenue.timeslots.push(slot);
      } else {
        acc.push({
          name: slot.name,
          venue_id: slot.venue_id,
          timeslots: [slot]
        });
      }
      return acc;
    }, [] as GroupedTimeslot[]);
  }

  get prefer_timeslots(): FormArray {
    return this.schoolEnrollmentForm.get('prefer_timeslots') as FormArray;
  }

  hasTimeslot(timeslotId: number): boolean {
    return this.prefer_timeslots.controls.some(control => control.value === timeslotId);
  }

  onTimeslotToggle(event: any, timeslotId: number): void {
    if (event.target.checked) {
      this.prefer_timeslots.push(this.fb.control(timeslotId));
    } else {
      const index = this.prefer_timeslots.controls.findIndex(control => control.value === timeslotId);
      if (index !== -1) {
        this.prefer_timeslots.removeAt(index);
      }
    }
    this.prefer_timeslots.markAsTouched();
  }

  get selectedTimeslotLabels(): string[] {
    const selectedIds = this.prefer_timeslots.value;
    const labels: string[] = [];
    this.groupedTimeslots.forEach(group => {
      group.timeslots.forEach(timeslot => {
        if (selectedIds.includes(timeslot.id)) {
          labels.push(`${group.name} - ${this.translate.instant(this.getWeekdayLabel(timeslot.weekday))} ${timeslot.label}`);
        }
      });
    });
    return labels;
  }
  get selectedVenueDetails() {
    const selectedIds = this.prefer_timeslots.value;
    const result: any[] = [];
    this.groupedTimeslots.forEach(group => {
      const venueTimeslots = group.timeslots.filter(ts => selectedIds.includes(ts.id));
      if (venueTimeslots.length > 0) {
        result.push({
          venue_id: group.venue_id, 
          nameKey: `SCHOOL_ENROLLMENT.VENUE_${group.venue_id}_NAME`,
          addressLabelKey: `SCHOOL_ENROLLMENT.VENUE_${group.venue_id}_ADDRESS_LABEL`,
          addressValueKey: `SCHOOL_ENROLLMENT.VENUE_${group.venue_id}_ADDRESS_VALUE`,
          telLabelKey: `SCHOOL_ENROLLMENT.VENUE_${group.venue_id}_TEL_LABEL`,
          telValueKey: `SCHOOL_ENROLLMENT.VENUE_${group.venue_id}_TEL_VALUE`,
          selectedTimes: venueTimeslots.map(ts => ({
            weekday: this.translate.instant(this.getWeekdayLabel(ts.weekday)),
            label: ts.label
          }))
        });
      }
    });

    return result; 
  }
  getWeekdayLabel(weekday: number): string {
    const weekdayMap: { [key: number]: string } = {
      1: 'DAYS.MONDAY',
      2: 'DAYS.TUESDAY',
      3: 'DAYS.WEDNESDAY',
      4: 'DAYS.THURSDAY',
      5: 'DAYS.FRIDAY',
      6: 'DAYS.SATURDAY',
      7: 'DAYS.SUNDAY',
    };
    return weekdayMap[weekday as keyof typeof weekdayMap] || '';
  }

  isFirstTimeslotOfDay(currentTimeslot: Timeslot, groupTimeslots: Timeslot[]): boolean {
    const currentIndex = groupTimeslots.indexOf(currentTimeslot);
    return currentIndex === 0 || groupTimeslots?.[currentIndex - 1]?.weekday !== currentTimeslot.weekday;
  }

  onSubmit(): void {
    if (this.schoolEnrollmentForm.valid) {
      const formValue = this.schoolEnrollmentForm.value;

      const selectedPhysicallyChallenged = Object.keys(formValue.physically_challenged_group)
        .filter(key => formValue.physically_challenged_group?.[key])
        .join(',');

      const selectedIntellectuallyChallenged = Object.keys(formValue.intellectually_challenged_group)
        .filter(key => formValue.intellectually_challenged_group?.[key])
        .map(key => {
          if (key === 'intellectually_challenged_sub') {
            return 'intellectually_challenged';
          }
          return key;
        })
        .join(',');


      let preferVenue: number | undefined;
      const firstSelectedTimeslotId = this.prefer_timeslots.value?.[0];

      for (const group of this.groupedTimeslots) {
        if (group.timeslots.some(slot => slot.id === firstSelectedTimeslotId)) {
          preferVenue = group.venue_id;
          break;
        }
      }
      
      this.applicationPayload = {
        organization: formValue.organization,
        address: formValue.address,
        tel: formValue.tel,
        fax: formValue.fax,
        contact_person: formValue.contact_person,
        email: formValue.email,
        website: formValue.website,
        physically_challenged: selectedPhysicallyChallenged,
        intellectually_challenged: selectedIntellectuallyChallenged,
        age_range: formValue.age_range,
        
        term_id: this.currentTerm?.id || 0,
        // term_id: Array.from(uniqueVenueIds), 
        type: formValue.type,
        prefer_venue: preferVenue!,
        prefer_timeslots: this.prefer_timeslots.value
      };

      this.showPreview = true;
    } else {
      this.schoolEnrollmentForm.markAllAsTouched();
      alert(this.translate.instant('SCHOOL_ENROLLMENT.INVALID_FORM_ALERT'));
      console.log('Form is invalid. Please check all fields.');
    }
  }

  submitApplication(): void {
    if (this.applicationPayload) {
      console.log('Final Payload to API:', this.applicationPayload);

      this.authService.createApplication(this.applicationPayload).subscribe({
        next: (response: CreateApplicationResponse) => {
          if (response.status === 'ok') {
            // alert('' + (response.message || ''));
            console.log('Application created successfully:', response);
            this.schoolEnrollmentForm.reset();
            while (this.prefer_timeslots.length !== 0) {
              this.prefer_timeslots.removeAt(0);
            }
            this.showPreview = false;
            this.applicationPayload = null;
            this.router.navigate(['/application-submitted']);
          } else {
            alert('' + (response.errors || response.message || '未知错误'));
            console.error('Application creation failed:', response.errors || response.message);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error submitting application:', error);
          let errorMessage = this.translate.instant('SCHOOL_ENROLLMENT.UNKNOWN_ERROR_SUBMITTING'); 
          if (error.error && typeof error.error === 'object') {
            if (error.error.error) { 
              errorMessage = error.error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            } else if (error.error.errors) {
              const errors = error.error.errors;
              const detailedMessages: string[] = [];
              for (const key in errors) {
                if (errors.hasOwnProperty(key) && Array.isArray(errors?.[key])) {
                  detailedMessages.push(...errors[key].map((msg: string) => `${key}: ${msg}`));
                }
              }
              if (detailedMessages.length > 0) {
                errorMessage = detailedMessages.join('\n');
              }
            }
          } else if (typeof error.error === 'string') { 
            errorMessage = error.error;
          }
          
          alert(errorMessage);
        }
      });
    }
  }

  closePreview(): void {
    this.showPreview = false;
    this.applicationPayload = null;
  }

  getFormControlValue(controlName: string): any {
    return this.schoolEnrollmentForm.get(controlName)?.value;
  }

  getSelectedPhysicallyChallengedLabels(): string[] {
    return this.physicallyChallengedOptions
      .filter(option => this.schoolEnrollmentForm.get('physically_challenged_group')?.get(option.key)?.value)
      .map(option => this.translate.instant(option.labelKey));
  }

  getSelectedIntellectuallyChallengedLabels(): string[] {
    return this.intellectuallyChallengedOptions
      .filter(option => this.schoolEnrollmentForm.get('intellectually_challenged_group')?.get(option.key)?.value)
      .map(option => this.translate.instant(option.labelKey))
      .map(label => {
        if (label === this.translate.instant('SCHOOL_ENROLLMENT.INTELLECTUALLY_CHALLENGED_SUB')) {
          return this.translate.instant('SCHOOL_ENROLLMENT.INTELLECTUALLY_CHALLENGED_TITLE');
        }
        return label;
      });
  }
}